import React, { useEffect, useRef, useState } from 'react';
import { Search, SlidersHorizontal, Plus, Check } from 'lucide-react';
import { TemplateFormData, TemplateModule } from './types';
import { TemplateCreator } from '../../../components/marketing/templates/create-template';
import { Toggle } from '../../../components/marketing/templates/utiz';
import useRequest from '../../../components/hook/use-req';
import useWhatsappAccounts from '../../../components/hook/use-whatsapp-accounts';
import { FaEnvelope, FaLinkedin, FaSms, FaWhatsapp } from 'react-icons/fa';
import { RiMessage2Line } from 'react-icons/ri';
import Pagination from '../../../components/pagination';
import ReuseableDrawer from '../../../components/resuable-drawer';

// "All" plus every channel a template can carry — values are sent to the
// backend exactly as-is (matching how `channel` comes back on a template
// record, e.g. "WhatsApp"), so no case juggling happens before the request.
const CHANNEL_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'All', label: 'All Channels' },
  { value: 'WhatsApp', label: 'WhatsApp' },
  { value: 'Email', label: 'Email' },
  { value: 'SMS', label: 'SMS' },
  { value: 'RCS', label: 'RCS' },
];

const ChannelBadge: React.FC<{ type: string }> = ({ type }) => {
  const getChannelConfig = (channelType: string) => {
    switch (channelType?.toLowerCase()) {
      case 'whatsapp':
        return { bg: 'bg-green-500', icon: <FaWhatsapp className='text-white' /> };
      case 'email':
        return { bg: 'bg-blue-600', icon: <FaEnvelope className='text-white' /> };
      case 'linkedin':
        return { bg: 'bg-blue-700', icon: <FaLinkedin className='text-white' /> };
      case 'sms':
        return { bg: 'bg-indigo-500', icon: <FaSms className='text-white' /> };
      case 'rcs':
        return { bg: 'bg-blue-500', icon: <RiMessage2Line className='text-white' /> };
      default:
        return { bg: 'bg-green-500', icon: <FaWhatsapp className='text-white' /> };
    }
  };

  const c = getChannelConfig(type);
  return (
    <div className={`w-8 h-8 rounded-lg ${c.bg} flex items-center justify-center`}>{c.icon}</div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const getStatusConfig = (statusValue: string) => {
    switch (statusValue?.toUpperCase()) {
      case 'APPROVED':
        return { bg: 'bg-[#E7FECC] dark:bg-green-500/15', text: 'text-[#3A3428] dark:text-green-400', dot: 'bg-[#22C55E]', label: 'Approved' };
      case 'VERIFIED':
        return { bg: 'bg-green-50 dark:bg-green-500/15', text: 'text-[#3A3428] dark:text-green-400', dot: 'bg-green-500', label: 'Verified' };
      case 'IN_REVIEW':
      case 'PENDING':
        return { bg: 'bg-[#FEF0CD] dark:bg-amber-500/15', text: 'text-[#3A3428] dark:text-amber-400', dot: 'bg-[#F59E0B]', label: 'Pending Approval' };
      case 'REJECTED':
      case 'FAILED':
        return { bg: 'bg-[#FECDCD] dark:bg-red-500/15', text: 'text-[#3A3428] dark:text-red-400', dot: 'bg-[#FB6C24]', label: 'Failed' };
      default:
        return { bg: 'bg-gray-50 dark:bg-gray-700', text: 'text-gray-600 dark:text-gray-300', dot: 'bg-gray-500', label: statusValue || 'Unknown' };
    }
  };

  const cfg = getStatusConfig(status);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

const AllTemplates: React.FC = () => {
  const [showCreator, setShowCreator] = React.useState(false);
  const [templates, setTemplates] = React.useState<TemplateModule[]>([]);
  const { wabaAccounts } = useWhatsappAccounts();
  const token = localStorage.getItem("authToken");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const params = new URLSearchParams(new URL(window.location.href).search);
  const [currentPage, setCurrentPage] = useState(Number(params.get("page") || 1));
  const [totalPages, setTotalPages] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = React.useState<TemplateModule | null>(null);

  const [pageSize, setPageSize] = useState(Number(params.get("per_page")) || 10);
  const [channelFilter, setChannelFilter] = useState(params.get("channel") || "All");
  const [showFilters, setShowFilters] = useState(false);
  const { makeRequest: getTemplates, loading } = useRequest("/templates/", "GET", {
    Authorization: `Bearer ${token}`,
  });
  // GET /templates/{id} — the list row is enough to render the table, but
  // opening a template for view/edit fetches its own authoritative full
  // record (buttons, footer, etc.) instead of trusting whatever the list
  // response happened to include.
  const { makeRequest: getTemplateDetail } = useRequest("/templates", "GET", {
    Authorization: `Bearer ${token}`,
  });
  const [loadingTemplateId, setLoadingTemplateId] = useState<string | null>(null);
  // Guards against an in-flight, slower request (e.g. an unfiltered "All"
  // page still loading) landing AFTER a newer one (e.g. switching to the
  // "Email" filter) and silently overwriting its results — without this, a
  // filter change can appear to "load" the right data then quietly revert
  // to stale data once the older request finally resolves.
  const fetchIdRef = useRef(0);



  function updateUrlParams(params: { [key: string]: string | number | null }) {
    const url = new URL(window.location.href);
    Object.keys(params)?.forEach((key) => {
      if (params[key]) url.searchParams.set(key, params[key]!.toString());
      else url.searchParams.delete(key);
    });
    window.history.pushState({}, "", url.toString());
  }


  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    updateUrlParams({
      page: currentPage !== 1 ? currentPage : null,
      per_page: pageSize !== 10 ? pageSize : null,
      search: debouncedSearchTerm ? debouncedSearchTerm : null,
      channel: channelFilter !== "All" ? channelFilter : null,
      // status: activeFilters.verificationStatus !== "All" ? activeFilters.verificationStatus : null,

    });
    fetchTemplates();
  }, [currentPage, pageSize, debouncedSearchTerm, channelFilter]);

  const fetchTemplates = async () => {
    const requestId = ++fetchIdRef.current;
    const queryParams: Record<string, any> = {
      page: currentPage,
      limit: pageSize,
    };

    // Add search parameter
    if (debouncedSearchTerm.trim()) {
      queryParams.search = debouncedSearchTerm.trim();
    }
    if (channelFilter !== "All") {
      queryParams.channel = channelFilter;
    }
    const [response] = await getTemplates(undefined, queryParams);
    // A newer fetch (another filter/page change) already started — this
    // response is stale, drop it instead of overwriting the newer one.
    if (requestId !== fetchIdRef.current) return;
    if (response) {
      setTemplates(response.data);
      setTotalPages(Math.ceil((response.total || 0) / pageSize));
    } else if (response && Array.isArray(response)) {
      setTemplates(response);
    } else {
      setTemplates([]);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSelectChannelFilter = (channel: string) => {
    setChannelFilter(channel);
    setCurrentPage(1);
    setShowFilters(false);
  };

  const handleSelectTemplate = async (t: TemplateModule) => {
    setLoadingTemplateId(t.id);
    try {
      const [response, status] = await getTemplateDetail(undefined, undefined, t.id);
      // GET /templates/{id} wraps the actual record under `data`
      // ({status, message, data: {...}}), same envelope shape as most of
      // this API's other endpoints — unwrap it before using it.
      const detail = response?.data ?? response;
      // Fall back to the list row on failure — still opens with whatever
      // data we already had rather than blocking the user entirely.
      setSelectedTemplate(status >= 200 && status < 300 && detail?.id ? detail : t);
    } finally {
      setLoadingTemplateId(null);
    }
  };


  // const handleToggleStatus = async (template: TemplateModule) => {
  //   const newStatus = template.wa_status === 'APPROVED' ? 'IN_REVIEW' : 'APPROVED';

  //   try {
  //     const [response] = await updateTemplateStatus({
  //       template_id: template.id,
  //       status: newStatus
  //     });

  //     if (response) {
  //       toast.success(`Template ${newStatus === 'APPROVED' ? 'enabled' : 'disabled'} successfully`);
  //       // Update local state
  //       setTemplates(prev => prev.map(t => 
  //         t.id === template.id ? { ...t, wa_status: newStatus } : t
  //       ));
  //     } else {
  //       toast.error('Failed to update template status');
  //     }
  //   } catch (error) {
  //     toast.error('An error occurred');
  //   }
  // };

  const filtered = templates?.filter((t) =>
    t.template_name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) &&
    (channelFilter === "All" || t?.channel === channelFilter)
  ) || [];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 rounded-lg shadow p-4 sm:p-6 lg:p-8">
      <div className="mx-auto">
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-6">All Templates</h1>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
          <button
            type="button" onClick={() => setShowCreator(true)}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors shadow-sm flex-shrink-0"
          >
            <Plus size={16} /> New Template
          </button>
          <div className="flex-1 flex items-center gap-2 sm:ml-auto w-full sm:w-auto">
            <div className="relative flex-1 sm:max-w-xs">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500" />
              <input
                type="text" placeholder="Search Templates"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div className="relative flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowFilters((v) => !v)}
                className="flex items-center gap-2 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <SlidersHorizontal size={15} /> Filters
                {channelFilter !== "All" && (
                  <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400 text-[11px] font-semibold capitalize">
                    {channelFilter}
                  </span>
                )}
              </button>
              {showFilters && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowFilters(false)} />
                  <div className="absolute right-0 top-full mt-1.5 z-20 w-52 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden py-1.5">
                    <p className="px-3.5 pt-1 pb-1.5 text-[11px] font-semibold text-[#7B7B7B] font-semibold dark:text-gray-500 uppercase tracking-wide">
                      Channel
                    </p>
                    {CHANNEL_FILTER_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handleSelectChannelFilter(opt.value)}
                        className="w-full flex items-center justify-between gap-2 px-3.5 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 border-none cursor-pointer text-left"
                      >
                        <span>{opt.label}</span>
                        {channelFilter === opt.value && <Check size={14} className="text-indigo-600 dark:text-indigo-400" />}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden shadow-sm">
          <div className="hidden md:grid grid-cols-[2fr_1fr_1.5fr_1.2fr_1fr_1fr] gap-4 px-6 py-3 border-b border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/60">
            {['Templates', 'Channels', 'Approval Status', 'Category', 'Language', 'Enable/ Disable'].map((h) => (
              <p key={h} className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{h}</p>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">
              Loading templates...
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">
              {debouncedSearchTerm ? 'No templates found matching your search' : 'No templates available. Create your first template!'}
            </div>
          ) : (
            filtered.map((t, i) => (
              <div
                key={t.id}
                onClick={() => handleSelectTemplate(t)}
                className={`grid grid-cols-2 md:grid-cols-[2fr_1fr_1.5fr_1.2fr_1fr_1fr] cursor-pointer gap-4 px-6 py-4 items-center hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors ${i !== filtered.length - 1 ? 'border-b border-gray-100 dark:border-gray-800' : ''} ${loadingTemplateId === t.id ? 'opacity-50 pointer-events-none' : ''}`}
              >
                <p className="text-sm font-medium capitalize text-gray-800 dark:text-gray-100 col-span-2 md:col-span-1 truncate" title={t.template_name}>
                  {t.template_name}
                </p>
                <div className="flex items-center">
                  <ChannelBadge type={t.channel || 'whatsapp'} />
                </div>
                <div>
                  <StatusBadge status={t.wa_status} />
                </div>
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t.category || 'Not specified'}
                </p>
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t.language || 'en_US'}
                </p>
                <div className="flex items-center">
                  <Toggle
                    checked={t.wa_status === 'APPROVED'}
                    onChange={() => (t)}
                  />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <ReuseableDrawer
        isOpen={showCreator || !!selectedTemplate}
        onClose={() => { setShowCreator(false); setSelectedTemplate(null); }}
        title={selectedTemplate ? selectedTemplate.template_name : "New Template"}
        initialWidth="60%"
      >
        <TemplateCreator
          isOpen={showCreator || !!selectedTemplate}
          selectedTemplate={selectedTemplate}
          templates={templates}
          wabaAccounts={wabaAccounts}
          onClose={() => { setShowCreator(false); setSelectedTemplate(null); }}
          onSaveDraft={() => { setShowCreator(false); setSelectedTemplate(null); fetchTemplates(); }}
          onSendRequest={() => { setShowCreator(false); setSelectedTemplate(null); fetchTemplates(); }}
          asDrawer
        />
      </ReuseableDrawer>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
        />
      )}
    </div>
  );
};

export { TemplateCreator, AllTemplates };
export type { TemplateFormData, TemplateModule };
export default AllTemplates;